import React, { useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, input, btn, btnGhost, SvgViewer, downloadBlob } from "./_drawingsCommon";

export default function PlotPlanPanel({ projectId }) {
  const [plotW, setPlotW] = useState(60);
  const [plotH, setPlotH] = useState(40);
  const [runId, setRunId] = useState("");
  const [drawingNo, setDrawingNo] = useState("");
  const [revision, setRevision] = useState("0");
  const [classified, setClassified] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [filename, setFilename] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const classify = async () => {
    setErr(""); setClassified(null); setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/plot-plan/classify`,
        { run_id: runId || undefined });
      setClassified(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally { setBusy(false); }
  };

  const generate = async () => {
    setErr(""); setBlobUrl(null); setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/plot-plan.svg`,
        { plot_size_m: [Number(plotW), Number(plotH)], run_id: runId || undefined,
          drawing_no: drawingNo || undefined, revision },
        { responseType: "blob" });
      const fn = `plot-plan-${revision}.svg`;
      setFilename(fn);
      setBlobUrl(URL.createObjectURL(data));
    } catch (e) {
      try {
        const txt = await e?.response?.data?.text?.();
        setErr(formatErr(txt && JSON.parse(txt).detail) || String(e));
      } catch { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="plot-plan-panel" style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 12 }}>
      <div style={card}>
        <div style={label}>Plot Size (m)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          <input style={input} type="number" data-testid="pp-w" placeholder="Width"
            value={plotW} onChange={(e) => setPlotW(e.target.value)} />
          <input style={input} type="number" data-testid="pp-h" placeholder="Height"
            value={plotH} onChange={(e) => setPlotH(e.target.value)} />
        </div>

        <div style={{ height: 10 }} />
        <div style={label}>Source</div>
        <input style={input} data-testid="pp-run-id" placeholder="Run ID (optional — uses sample if empty)"
          value={runId} onChange={(e) => setRunId(e.target.value)} />

        <div style={{ height: 10 }} />
        <div style={label}>Drawing Meta</div>
        <input style={input} data-testid="pp-drawing-no" placeholder="Drawing No (optional)"
          value={drawingNo} onChange={(e) => setDrawingNo(e.target.value)} />
        <div style={{ height: 6 }} />
        <input style={input} data-testid="pp-revision" placeholder="Revision"
          value={revision} onChange={(e) => setRevision(e.target.value)} />

        <div style={{ height: 10 }} />
        <button style={{ ...btnGhost, width: "100%" }} data-testid="pp-classify" onClick={classify} disabled={busy}>
          Classify Hazardous Sources
        </button>
        <div style={{ height: 6 }} />
        <button style={{ ...btn, width: "100%" }} data-testid="pp-generate" onClick={generate} disabled={busy}>
          {busy ? "Generating…" : "Generate Plot Plan SVG"}
        </button>
        {blobUrl && (
          <button style={{ ...btnGhost, width: "100%", marginTop: 6 }}
            data-testid="pp-download" onClick={() => downloadBlob(blobUrl, filename)}>
            Download {filename}
          </button>
        )}
        {err && <div style={{ marginTop: 8, padding: 8, fontSize: 11, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4 }}>{err}</div>}

        {classified && (
          <div style={{ marginTop: 10, padding: 8, background: "var(--pf-surface-2)", borderRadius: 4, fontSize: 11 }} data-testid="pp-classify-result">
            <div style={{ ...label, marginBottom: 4 }}>Hazard Schedule — {classified.classified.length} of {classified.equipment_count}</div>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {classified.classified.map((c, i) => (
                <div key={i} style={{ padding: "4px 0", borderBottom: "1px solid var(--pf-border)" }}>
                  <div style={{ fontWeight: 700, color: "#F26B1F" }}>{c.tag}</div>
                  <div style={{ color: "var(--pf-muted)" }}>{c.rule} · Z1={c.zone1_r}m · Z2={c.zone2_r}m</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <SvgViewer blobUrl={blobUrl} testId="pp-svg" emptyText="Click Generate to render the plot plan with hazardous-area zones (API 505 / IEC 60079-10-1)." />
    </div>
  );
}
